/**
 * ============================================================================
 * RecordPMDialog — Phase 6e frontend (Equipment & Validation).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: capture a new PM record. Includes:
 *            - performed-at timestamp
 *            - PM type picker (ROUTINE / CORRECTIVE / PREVENTIVE /
 *                              BREAKDOWN_REPAIR)
 *            - work description text area
 *            - parts_replaced JSON array editor
 *            - outcome picker (COMPLETED / PARTIAL / DEFERRED / FAILED)
 *            - engineer signature
 *            - doc-controller co-sign (required when outcome = COMPLETED)
 *
 * A11Y: parts editor accepts either a JSON array or a blank line so AT
 *  users aren't forced through JSON for a no-parts PM.
 * ============================================================================ */

import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import dialogStyles from '../AuthorWorkbench/NewDocumentForm.module.css';
import styles from './PMTray.module.css';
import {
  useRecordPMMutation,
  type RecordPMInput,
} from './useRecordPMMutation';
import type { PMTask } from './usePMTrayQuery';
import type {
  PMOutcome,
  PMType,
} from '../EquipmentDetail/useEquipmentDetailQuery';

const PM_TYPES: PMType[] = [
  'ROUTINE',
  'CORRECTIVE',
  'PREVENTIVE',
  'BREAKDOWN_REPAIR',
];

const OUTCOMES: PMOutcome[] = ['COMPLETED', 'PARTIAL', 'DEFERRED', 'FAILED'];

export interface RecordPMDialogProps {
  task: PMTask;
  onClose: () => void;
  onRecorded: () => void;
}

export function RecordPMDialog({
  task,
  onClose,
  onRecorded,
}: RecordPMDialogProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const descId = useId();
  const firstRef = useRef<HTMLInputElement | null>(null);

  const recordM = useRecordPMMutation();
  const nowIso = new Date().toISOString().slice(0, 16);
  const [performedAt, setPerformedAt] = useState<string>(nowIso);
  const [pmType, setPmType] = useState<PMType>('ROUTINE');
  const [outcome, setOutcome] = useState<PMOutcome>('COMPLETED');
  const [workDesc, setWorkDesc] = useState('');
  const [partsText, setPartsText] = useState<string>(
    '[\n  { "part_no": "P-001", "name": "HEPA filter" }\n]',
  );
  const [engineerSig, setEngineerSig] = useState('');
  const [docCtrlSig, setDocCtrlSig] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    firstRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function onSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    setError(null);

    let parts: Array<Record<string, unknown>>;
    const trimmed = partsText.trim();
    if (trimmed === '' || trimmed === '[]') {
      parts = [];
    } else {
      try {
        const parsed: unknown = JSON.parse(trimmed);
        if (!Array.isArray(parsed)) {
          setError(
            t('equipment.pmTray.dialog.errors.partsArray', {
              defaultValue: 'Parts replaced must be a JSON array.',
            }),
          );
          return;
        }
        for (const item of parsed) {
          if (
            item === null ||
            typeof item !== 'object' ||
            Array.isArray(item)
          ) {
            setError(
              t('equipment.pmTray.dialog.errors.partsObjects', {
                defaultValue:
                  'Each parts entry must be a JSON object.',
              }),
            );
            return;
          }
        }
        parts = parsed as Array<Record<string, unknown>>;
      } catch {
        setError(
          t('equipment.pmTray.dialog.errors.partsJson', {
            defaultValue: 'Parts replaced must be valid JSON.',
          }),
        );
        return;
      }
    }

    if (!workDesc.trim()) {
      setError(
        t('equipment.pmTray.dialog.errors.workRequired', {
          defaultValue: 'Work description is required.',
        }),
      );
      return;
    }
    if (!engineerSig.trim()) {
      setError(
        t('equipment.pmTray.dialog.errors.engineerSig', {
          defaultValue: 'Engineer signature bundle id is required.',
        }),
      );
      return;
    }
    if (outcome === 'COMPLETED' && !docCtrlSig.trim()) {
      setError(
        t('equipment.pmTray.dialog.errors.docCtrlSig', {
          defaultValue:
            'A COMPLETED outcome requires a doc-controller co-signature.',
        }),
      );
      return;
    }
    if (
      outcome === 'COMPLETED' &&
      docCtrlSig.trim() === engineerSig.trim()
    ) {
      setError(
        t('equipment.pmTray.dialog.errors.distinctSigs', {
          defaultValue:
            'Engineer and doc-controller signature bundles must be distinct.',
        }),
      );
      return;
    }

    const input: RecordPMInput = {
      equipmentId: task.equipment_id,
      performed_at_utc: new Date(performedAt).toISOString(),
      pm_type: pmType,
      outcome,
      work_description: workDesc.trim(),
      parts_replaced: parts,
      engineer_signature_bundle_id: engineerSig.trim(),
      doc_controller_signature_bundle_id:
        outcome === 'COMPLETED' ? docCtrlSig.trim() : undefined,
    };

    try {
      await recordM.mutateAsync(input);
      onRecorded();
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : t('equipment.pmTray.dialog.errors.serverFailed', {
              defaultValue: 'Server rejected the PM record.',
            }),
      );
    }
  }

  return (
    <div
      className={dialogStyles.backdrop}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={dialogStyles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
      >
        <header className={dialogStyles.header}>
          <h2 id={titleId} className={dialogStyles.title}>
            {t('equipment.pmTray.dialog.title', {
              defaultValue: 'Record PM',
            })}
          </h2>
          <p id={descId} className={dialogStyles.desc}>
            {t('equipment.pmTray.dialog.subtitle', {
              defaultValue:
                'Record a preventive-maintenance event for {{tag}} ({{name}}). A COMPLETED outcome requires a doc-controller co-signature.',
              tag: task.equipment_tag,
              name: task.equipment_name,
            })}
          </p>
        </header>

        <form className={dialogStyles.form} onSubmit={onSubmit} noValidate>
          {error && (
            <div role="alert" className={dialogStyles.errorBanner}>
              {error}
            </div>
          )}

          <div className={styles.twoCol}>
            <div className={dialogStyles.field}>
              <label htmlFor="pm-performed" className={dialogStyles.label}>
                {t('equipment.pmTray.dialog.performedAt', {
                  defaultValue: 'Performed at',
                })}
                <span className={dialogStyles.required} aria-hidden="true">*</span>
              </label>
              <input
                id="pm-performed"
                ref={firstRef}
                type="datetime-local"
                className={dialogStyles.input}
                value={performedAt}
                onChange={(e) => setPerformedAt(e.target.value)}
                required
              />
            </div>
            <div className={dialogStyles.field}>
              <label htmlFor="pm-type" className={dialogStyles.label}>
                {t('equipment.pmTray.dialog.type', {
                  defaultValue: 'PM type',
                })}
              </label>
              <select
                id="pm-type"
                className={dialogStyles.select}
                value={pmType}
                onChange={(e) => setPmType(e.target.value as PMType)}
              >
                {PM_TYPES.map((p) => (
                  <option key={p} value={p}>
                    {t(`equipment.detail.pm.type.${p}`, { defaultValue: p })}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className={dialogStyles.field}>
            <label htmlFor="pm-outcome" className={dialogStyles.label}>
              {t('equipment.pmTray.dialog.outcome', { defaultValue: 'Outcome' })}
              <span className={dialogStyles.required} aria-hidden="true">*</span>
            </label>
            <select
              id="pm-outcome"
              className={dialogStyles.select}
              value={outcome}
              onChange={(e) => setOutcome(e.target.value as PMOutcome)}
            >
              {OUTCOMES.map((o) => (
                <option key={o} value={o}>
                  {t(`equipment.detail.pm.outcome.${o}`, { defaultValue: o })}
                </option>
              ))}
            </select>
          </div>

          <div className={dialogStyles.field}>
            <label htmlFor="pm-work" className={dialogStyles.label}>
              {t('equipment.pmTray.dialog.workDescription', {
                defaultValue: 'Work description',
              })}
              <span className={dialogStyles.required} aria-hidden="true">*</span>
            </label>
            <textarea
              id="pm-work"
              className={styles.workArea}
              value={workDesc}
              rows={4}
              onChange={(e) => setWorkDesc(e.target.value)}
              required
            />
          </div>

          <div className={dialogStyles.field}>
            <label htmlFor="pm-parts" className={dialogStyles.label}>
              {t('equipment.pmTray.dialog.partsReplaced', {
                defaultValue: 'Parts replaced (JSON array)',
              })}
            </label>
            <textarea
              id="pm-parts"
              className={styles.partsArea}
              value={partsText}
              onChange={(e) => setPartsText(e.target.value)}
              aria-describedby="pm-parts-hint"
            />
            <p id="pm-parts-hint" className={styles.hint}>
              {t('equipment.pmTray.dialog.partsHint', {
                defaultValue:
                  'JSON array of part objects (e.g. {"part_no": "P-001", "name": "HEPA filter"}). Leave empty or use [] for none.',
              })}
            </p>
          </div>

          <div className={dialogStyles.field}>
            <label htmlFor="pm-eng" className={dialogStyles.label}>
              {t('equipment.pmTray.dialog.engineerSig', {
                defaultValue: 'Engineer signature bundle id',
              })}
              <span className={dialogStyles.required} aria-hidden="true">*</span>
            </label>
            <input
              id="pm-eng"
              className={dialogStyles.input}
              value={engineerSig}
              onChange={(e) => setEngineerSig(e.target.value)}
              placeholder="sig-…"
              required
            />
          </div>

          {outcome === 'COMPLETED' && (
            <div className={dialogStyles.field}>
              <label htmlFor="pm-doc" className={dialogStyles.label}>
                {t('equipment.pmTray.dialog.docCtrlSig', {
                  defaultValue: 'Doc-controller signature bundle id',
                })}
                <span className={dialogStyles.required} aria-hidden="true">*</span>
              </label>
              <input
                id="pm-doc"
                className={dialogStyles.input}
                value={docCtrlSig}
                onChange={(e) => setDocCtrlSig(e.target.value)}
                placeholder="sig-…"
                required
              />
            </div>
          )}

          <footer className={dialogStyles.footer}>
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={recordM.isPending}
            >
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button type="submit" variant="primary" isLoading={recordM.isPending}>
              {t('equipment.pmTray.dialog.submit', { defaultValue: 'Record' })}
            </Button>
          </footer>
        </form>
      </div>
    </div>
  );
}
